import { Fragment, useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';

interface PrivacyScope { key: string; label?: string; title?: string; name?: string; detail?: string; description?: string; recommended?: boolean; default?: boolean }
interface PrivacySection { heading?: string; title?: string; content?: string; items?: string[]; paragraphs?: string[] }
interface PrivacyNotice {
  version: string;
  updatedAt?: string;
  title: string;
  summary?: string;
  highlights?: string[];
  scopes?: PrivacyScope[];
  sections?: PrivacySection[];
}
interface ConsentState { agreed: boolean; docVersion: string; scopes: string[]; agreedAt?: string; revokedAt?: string; storage?: string }

// 本地缓存「已同意的声明版本」：命中时首屏直接放行，不再闪现授权弹层。
// 服务端仍是权威——后台校验发现版本变化或未同意时会清缓存并重新弹出。
const CONSENT_CACHE_KEY = 'caigen.privacy.agreed:v1';
function readConsentCache(): { version: string; scopes: string[] } | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONSENT_CACHE_KEY) || 'null');
    if (parsed && typeof parsed.version === 'string') return { version: parsed.version, scopes: Array.isArray(parsed.scopes) ? parsed.scopes : [] };
  } catch { /* 缓存不可用时按未同意处理 */ }
  return null;
}
function writeConsentCache(version: string, scopes: string[]) {
  try { window.localStorage.setItem(CONSENT_CACHE_KEY, JSON.stringify({ version, scopes })); } catch { /* 忽略 */ }
}
function clearConsentCache() {
  try { window.localStorage.removeItem(CONSENT_CACHE_KEY); } catch { /* 忽略 */ }
}

function RichLine({ children }: { children: string }) {
  return <>{children.split(/(\*\*.*?\*\*)/g).map((part, index) => part.startsWith('**') && part.endsWith('**')
    ? <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
    : <Fragment key={`${part}-${index}`}>{part}</Fragment>)}</>;
}

export function PrivacyConsent({ mode = 'settings' }: { mode?: 'gate' | 'settings' }) {
  const [notice, setNotice] = useState<PrivacyNotice>();
  const [consent, setConsent] = useState<ConsentState>();
  const [selected, setSelected] = useState<string[]>(['auto_public']);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [cached, setCached] = useState(() => (mode === 'gate' ? readConsentCache() : null));

  const load = useCallback(async () => {
    try {
      const noticeResponse = await api<{ data: PrivacyNotice }>('/api/v1/privacy/notice');
      const currentNotice = noticeResponse.data;
      setNotice(currentNotice);
      const consentResponse = await api<{ data: ConsentState }>(`/api/v1/privacy/consent?docVersion=${encodeURIComponent(currentNotice.version)}`);
      setConsent(consentResponse.data);
      if (consentResponse.data.agreed) {
        writeConsentCache(currentNotice.version, consentResponse.data.scopes || []);
        setCached({ version: currentNotice.version, scopes: consentResponse.data.scopes || [] });
        setSelected(consentResponse.data.scopes || []);
      } else {
        clearConsentCache();
        setCached(null);
        const defaults = (currentNotice.scopes || []).filter((scope) => scope.default || scope.recommended || scope.key === 'auto_public').map((scope) => scope.key);
        setSelected(defaults.filter((key) => key !== 'auto_personal'));
      }
    } catch (requestError) {
      // 读不到状态时不要弹授权（避免网络抖动导致每页弹窗），交给设置页兜底
      setCached(null);
      setError(requestError instanceof Error ? requestError.message : '隐私声明暂时无法读取');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (scopes: string[]) => {
    if (!notice) return;
    setSaving(true);
    setError('');
    try {
      const response = await api<{ data: ConsentState }>('/api/v1/privacy/consent', {
        method: 'POST', body: JSON.stringify({ docVersion: notice.version, scopes }),
      });
      setConsent(response.data);
      setSelected(response.data.scopes || scopes);
      writeConsentCache(notice.version, response.data.scopes || scopes);
      setCached({ version: notice.version, scopes: response.data.scopes || scopes });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '保存同意失败');
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!notice) return;
    setSaving(true);
    try {
      await api(`/api/v1/privacy/consent?docVersion=${encodeURIComponent(notice.version)}`, { method: 'DELETE' });
      setConsent({ agreed: false, docVersion: notice.version, scopes: [] });
      clearConsentCache();
      setCached(null);
      setConfirmRevoke(false);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '撤回失败');
    } finally { setSaving(false); }
  };

  if (mode === 'gate') {
    // 已同意（本地缓存命中，或服务端已确认）→ 一律不渲染，切页零闪烁
    if (cached || consent?.agreed) return null;
    // 状态未就绪：保持静默，不显示「正在读取」弹层
    if (!notice || !consent) return null;
    // 只有确认「未同意 / 版本已更新 / 请求失败」才弹出
    if (!error && consent.agreed) return null;
  }
  if (!notice) return mode === 'settings' ? <section className="paper-card privacy-card privacy-loading"><span>隐</span><p>{error || '正在读取隐私声明…'}</p></section> : null;

  const content = <section className={`paper-card privacy-card${mode === 'gate' ? ' privacy-dialog' : ''}`} id={mode === 'settings' ? 'data-permission' : undefined} role={mode === 'gate' ? 'dialog' : undefined} aria-modal={mode === 'gate' ? true : undefined} aria-labelledby={`privacy-title-${mode}`}>
    <header className="privacy-head">
      <div className={`privacy-seal${consent?.agreed ? ' agreed' : ''}`}><b>{consent?.agreed ? '允' : '隐'}</b><small>{notice.version}</small></div>
      <div><small>DATA · CONSENT</small><h2 id={`privacy-title-${mode}`}>{notice.title}</h2><p>{notice.summary}</p></div>
      <span className={consent?.agreed ? 'on' : ''}>{consent?.agreed ? '已同意' : '待确认'}</span>
    </header>

    <div className="privacy-body">
      {notice.highlights?.length ? <div className="privacy-highlights">{notice.highlights.map((item, index) => <p key={item}><i>{index + 1}</i><RichLine>{item}</RichLine></p>)}</div> : null}

      <fieldset className="privacy-scopes" disabled={saving}>
        <legend>授权范围由你决定</legend>
        {(notice.scopes || []).map((scope) => <label key={scope.key} className={selected.includes(scope.key) ? 'selected' : ''}>
          <input type="checkbox" checked={selected.includes(scope.key)} onChange={() => setSelected((current) => current.includes(scope.key) ? current.filter((key) => key !== scope.key) : [...current, scope.key])} />
          <span><b>{scope.label || scope.title || scope.name || scope.key}{scope.key === 'auto_public' ? <em>建议</em> : null}</b><small>{scope.detail || scope.description}</small></span>
        </label>)}
      </fieldset>

      {notice.sections?.length ? <details className="privacy-details"><summary>查看完整声明与数据边界</summary><div>{notice.sections.map((section) => <section key={section.heading || section.title}><h3>{section.heading || section.title}</h3>{(section.items || section.paragraphs || (section.content ? [section.content] : [])).map((paragraph) => <p key={paragraph}><RichLine>{paragraph}</RichLine></p>)}</section>)}</div></details> : null}

      {error ? <p className="privacy-error" role="alert">{error}</p> : null}
    </div>
    <footer className="privacy-actions">
      <p>个人数据默认手动填写。菜根不收取学校密码，也不会替你保存 CAS 登录凭据。</p>
      <div>
        {consent?.agreed && mode === 'settings' ? (confirmRevoke ? <><button className="quiet-button danger" disabled={saving} onClick={() => void revoke()}>确认撤回</button><button className="quiet-button" onClick={() => setConfirmRevoke(false)}>取消</button></> : <button className="quiet-button" onClick={() => setConfirmRevoke(true)}>撤回同意</button>) : <button className="quiet-button" disabled={saving} onClick={() => void save([])}>仅使用手动填写</button>}
        <button className="primary-button" disabled={saving} onClick={() => void save(selected)}>{consent?.agreed ? '更新授权范围' : '同意所选范围'}</button>
      </div>
    </footer>
  </section>;

  return mode === 'gate' ? <div className="privacy-gate">{content}</div> : content;
}
